<div class="legal-page">
  <div class="legal-container">
    <p class="legal-intro">
      These Terms of Service ("Terms") govern access to and use of the dAIrector
      platform ("dAIrector", "we", "us", or "our"), owned and operated by High Giraffe Media Pvt Ltd.
      By creating an account, accessing the platform, or using any part of the service, you agree to
      be bound by these Terms.
    </p>

    <section class="legal-section">
      <div class="section-header">
        <span class="section-number">01</span>
        <h2 class="section-title">Role of dAIrector</h2>
      </div>
      <div class="section-content">
        <p>dAIrector is a production orchestration and automation platform that enables users to
          plan, generate, manage, and export media assets using structured workflows and
          AI-assisted systems.</p>
        <p>We provide infrastructure, tooling, and automated execution logic. We do not act as a
          creative author, legal reviewer, or rights verifier for user-provided inputs or
          generated outputs.</p>
      </div>
    </section>

    <section class="legal-section">
      <div class="section-header">
        <span class="section-number">02</span>
        <h2 class="section-title">Eligibility and Accounts</h2>
      </div>
      <div class="section-content">
        <p>You must be legally capable of entering into a binding contract to use dAIrector.</p>
        <p>Accounts may be created by individuals or on behalf of organizations. The entity creating
          an Organization within dAIrector is responsible for all activity occurring under that
          Organization, including actions taken by invited collaborators.</p>
      </div>
    </section>

    <section class="legal-section">
      <div class="section-header">
        <span class="section-number">03</span>
        <h2 class="section-title">Organization and Collaboration Responsibility</h2>
      </div>
      <div class="section-content">
        <p>Organization owners and administrators are responsible for:</p>
        <ul class="legal-list">
          <li>Managing user access and permissions</li>
          <li>Ensuring collaborators act in compliance with these Terms</li>
          <li>All content created, generated, or exported within the Organization</li>
        </ul>
        <p>dAIrector is not responsible for internal disputes between Organization members or
          collaborators.</p>
      </div>
    </section>

    <section class="legal-section">
      <div class="section-header">
        <span class="section-number">04</span>
        <h2 class="section-title">User Inputs and Responsibility</h2>
      </div>
      <div class="section-content">
        <p>You represent and warrant that you have all necessary rights, permissions, and authority
          to provide any inputs uploaded or submitted to dAIrector, including but not limited to:
        </p>
        <ul class="legal-list">
          <li>Scripts, text, and creative briefs</li>
          <li>Brand assets and logos</li>
          <li>Visual references</li>
          <li>Voice recordings or voice likenesses</li>
          <li>Music or sound references</li>
        </ul>
        <p>You are solely responsible for ensuring that your inputs do not infringe any third-party
          rights or violate applicable laws.</p>
      </div>
    </section>

    <section class="legal-section">
      <div class="section-header">
        <span class="section-number">05</span>
        <h2 class="section-title">Output Ownership</h2>
      </div>
      <div class="section-content">
        <p>Subject to compliance with these Terms:</p>
        <div class="highlight-box ownership">
          <p>You retain <strong>100% ownership</strong> of all final outputs generated through
            dAIrector.</p>
          <p>Ownership is <strong>exclusive, worldwide, and perpetual</strong>, including full
            commercial exploitation rights.</p>
          <p>dAIrector does <strong>not</strong> claim any ownership, license, or reuse rights
            over final outputs.</p>
        </div>
        <p>Intermediate assets (such as drafts, metadata, storyboards, or previews) are generated
          solely to enable delivery of the final output and do not alter your ownership rights.
        </p>
      </div>
    </section>

    <section class="legal-section">
      <div class="section-header">
        <span class="section-number">06</span>
        <h2 class="section-title">AI-Assisted and Automated Generation</h2>
      </div>
      <div class="section-content">
        <p>dAIrector utilizes automated systems and AI-assisted processes to generate outputs based
          on user inputs, configurations, and approvals.</p>
        <p>You acknowledge that:</p>
        <ul class="legal-list">
          <li>Outputs are generated algorithmically and may vary based on inputs and system
            conditions</li>
          <li>dAIrector does not guarantee legal, regulatory, cultural, or industry compliance of
            outputs</li>
          <li>Final approval, publication, and use of outputs is your responsibility</li>
        </ul>
        <div class="highlight-box important">
          <p>Use of autonomous or semi-autonomous modes does not transfer responsibility or
            liability to dAIrector.</p>
        </div>
      </div>
    </section>

    <section class="legal-section">
      <div class="section-header">
        <span class="section-number">07</span>
        <h2 class="section-title">Third-Party Models and Services</h2>
      </div>
      <div class="section-content">
        <p>dAIrector integrates third-party AI models and infrastructure services to deliver
          functionality.</p>
        <p>While we select reputable providers, we do not control the underlying models and do not
          guarantee uninterrupted availability, specific behaviors, or outcomes produced by
          third-party services.</p>
        <p>No warranties are made regarding third-party systems.</p>
      </div>
    </section>

    <section class="legal-section">
      <div class="section-header">
        <span class="section-number">08</span>
        <h2 class="section-title">Acceptable Use</h2>
      </div>
      <div class="section-content">
        <p>You agree not to use dAIrector to:</p>
        <ul class="legal-list">
          <li>Violate any applicable laws or regulations</li>
          <li>Infringe intellectual property or privacy rights</li>
          <li>Generate or distribute unlawful, abusive, or deceptive content</li>
          <li>Attempt to access or disrupt systems or data not intended for you</li>
        </ul>
        <p>We reserve the right to suspend or terminate access for violations of this section.</p>
      </div>
    </section>

    <section class="legal-section">
      <div class="section-header">
        <span class="section-number">09</span>
        <h2 class="section-title">Service Availability and Changes</h2>
      </div>
      <div class="section-content">
        <p>dAIrector is provided on an "as is" and "as available" basis. Features, workflows, and
          capabilities may evolve over time.</p>
        <p>We may modify or discontinue parts of the service without prior notice.</p>
      </div>
    </section>

    <section class="legal-section">
      <div class="section-header">
        <span class="section-number">10</span>
        <h2 class="section-title">Limitation of Liability</h2>
      </div>
      <div class="section-content">
        <p>To the maximum extent permitted by law, dAIrector shall not be liable for any indirect,
          incidental, consequential, or special damages arising from use of the platform,
          including loss of profits, data, or business opportunities.</p>
        <p>Our total liability for any claim shall not exceed the fees paid by you to dAIrector in
          the twelve (12) months preceding the claim.</p>
      </div>
    </section>

    <section class="legal-section">
      <div class="section-header">
        <span class="section-number">11</span>
        <h2 class="section-title">Termination</h2>
      </div>
      <div class="section-content">
        <p>You may stop using the service at any time.</p>
        <p>We may suspend or terminate access if you violate these Terms or misuse the platform.
          Upon termination, access to the platform may be revoked, subject to applicable data
          retention policies.</p>
      </div>
    </section>

    <section class="legal-section">
      <div class="section-header">
        <span class="section-number">12</span>
        <h2 class="section-title">Governing Law</h2>
      </div>
      <div class="section-content">
        <p>These Terms are governed by and construed in accordance with the laws of India. Any
          disputes shall be subject to the exclusive jurisdiction of the courts located in India.
        </p>
      </div>
    </section>

    <section class="legal-section">
      <div class="section-header">
        <span class="section-number">13</span>
        <h2 class="section-title">Updates to These Terms</h2>
      </div>
      <div class="section-content">
        <p>We may update these Terms from time to time. Continued use of the platform after changes
          take effect constitutes acceptance of the revised Terms.</p>
      </div>
    </section>

    <section class="legal-section">
      <div class="section-header">
        <span class="section-number">14</span>
        <h2 class="section-title">Contact</h2>
      </div>
      <div class="section-content">
        <p>For questions regarding these Terms, please contact: <a href="mailto:legal@dairector.ai"
            class="contact-email">legal@dairector.ai</a></p>
      </div>
    </section>
  </div>
</div>
